import type { IDedicatedException, IResource } from "@gettactic/api";
import { resourceDedicatedExceptions } from "./dedicatedSeats";

/** "1 seat released", "3 seats released": how much of a shared workspace is free. */
export const releasedSeatsLabel = (count: number): string =>
  `${count} ${count === 1 ? "seat" : "seats"} released`;

/**
 * Owner names for a line with little room: the first `maxVisible` in full,
 * then how many more there are. "Ana, Ben +1".
 */
export const formatOwnerNames = (
  names: readonly string[],
  maxVisible = 2
): string => {
  const visible = names.slice(0, maxVisible).join(", ");
  const rest = names.length - maxVisible;
  return rest > 0 ? `${visible} +${rest}` : visible;
};

/**
 * The day every release ends on, so a shared workspace can still say
 * "available until"; null when they end on different days or there are none.
 */
export const sharedReleaseEndDay = (
  releases: readonly Pick<IDedicatedException, "ends_on">[]
): string | null => {
  const first = releases[0]?.ends_on;
  if (!first) return null;
  return releases.every((release) => release.ends_on === first) ? first : null;
};

const whoCanReserve = (
  releases: readonly Pick<IDedicatedException, "release">[]
): string => {
  const isSingle = releases.length === 1;
  const openCount = releases.filter((r) => r.release === "open").length;
  if (openCount === releases.length) {
    return isSingle ? "Anyone can reserve it." : "Anyone can reserve them.";
  }
  if (openCount === 0) {
    return isSingle
      ? "Only the people it's released to can reserve it."
      : "Only the people they're released to can reserve them.";
  }
  return "Some are open to anyone, others only to the people they're released to.";
};

/**
 * The note a booking screen shows on a shared workspace with released seats:
 * how many, whose they normally are, and who may take them.
 */
export const sharedReleaseNote = (
  releases: readonly Pick<IDedicatedException, "release">[],
  ownerNames: readonly string[]
): string => {
  const away =
    releases.length === 1
      ? "while its owner is away"
      : "while their owners are away";
  const owners =
    ownerNames.length > 0
      ? ` Normally dedicated to ${formatOwnerNames(ownerNames)}.`
      : "";
  return `${releasedSeatsLabel(releases.length)} ${away}.${owners} ${whoCanReserve(releases)}`;
};

interface OwnerName {
  id: string;
  name: string;
}

/**
 * Everyone a shared dedicated workspace belongs to on the day it was read.
 * Core lists only the owners holding their seat in the visible assignments;
 * an owner whose seat is released appears only on their release.
 */
export const dedicatedResourceOwners = (
  resource: Pick<IResource, "dedicated_exceptions" | "dedicated_exception"> & {
    visible_assignment?: OwnerName | null;
    user_visible_assignments?: readonly OwnerName[];
  }
): OwnerName[] => {
  const assigned = resource.user_visible_assignments ?? [];
  const holders =
    assigned.length > 0
      ? assigned
      : resource.visible_assignment
        ? [resource.visible_assignment]
        : [];
  const away = resourceDedicatedExceptions(resource).flatMap((release) =>
    release.dedicated_user ? [release.dedicated_user] : []
  );
  const owners = new Map<string, OwnerName>();
  for (const owner of [...holders, ...away]) {
    if (!owners.has(owner.id)) owners.set(owner.id, owner);
  }
  return [...owners.values()].map(({ id, name }) => ({ id, name }));
};
